import {isSpan, isTextBlock} from '@portabletext/schema'
import {applySetNode} from '../internal-utils/apply-set-node'
import {safeStringify} from '../internal-utils/safe-json'
import {getNode} from '../node-traversal/get-node'
import {isObjectNode} from '../slate/node/is-object-node'
import type {OperationImplementation} from './operation.types'

export const childUnsetOperationImplementation: OperationImplementation<
  'child.unset'
> = ({context, operation}) => {
  const blockKey = operation.at[0]._key
  const blockIndex = operation.editor.blockIndexMap.get(blockKey)

  if (blockIndex === undefined) {
    throw new Error(`Unable to find block index for block key ${blockKey}`)
  }

  const block =
    blockIndex !== undefined
      ? operation.editor.children.at(blockIndex)
      : undefined

  if (!block) {
    throw new Error(`Unable to find block at ${safeStringify(operation.at)}`)
  }

  if (!isTextBlock(context, block)) {
    throw new Error(`Block ${safeStringify(blockKey)} is not a text block`)
  }

  const childKey = operation.at[2]._key

  if (!childKey) {
    throw new Error(
      `Unable to find child key at ${safeStringify(operation.at)}`,
    )
  }

  const childIndex = block.children.findIndex(
    (child) => child._key === childKey,
  )

  if (childIndex === -1) {
    throw new Error(`Unable to find child at ${safeStringify(operation.at)}`)
  }

  const childEntry = getNode(operation.editor, [blockIndex, childIndex])

  if (!childEntry) {
    throw new Error(`Unable to find child at ${safeStringify(operation.at)}`)
  }

  const {node: child, path: childPath} = childEntry

  if (isSpan({schema: operation.editor.schema}, child)) {
    const newNode: Record<string, unknown> = {}

    for (const prop of operation.props) {
      if (prop === '_type') {
        // It's not allowed to unset the _type of a span
        continue
      }

      if (prop === '_key') {
        newNode['_key'] = context.keyGenerator()
        continue
      }

      newNode[prop] = null
    }

    applySetNode(operation.editor, newNode, childPath)

    return
  }

  if (isObjectNode({schema: operation.editor.schema}, child)) {
    const unsetProps: Record<string, unknown> = {}
    for (const prop of operation.props) {
      if (prop === '_type') {
        continue
      }
      if (prop === '_key') {
        unsetProps['_key'] = context.keyGenerator()
      } else {
        unsetProps[prop] = null
      }
    }
    applySetNode(operation.editor, unsetProps, childPath)

    return
  }

  throw new Error(
    `Unable to determine the type of child at ${safeStringify(operation.at)}`,
  )
}
